import {
  handleAtLeastSome,
  handleHook
} from '@shared/store/helpers/app-config/index.js'
import { useAuthStore } from '@shared/store/auth'
import { useConfigurationStore } from '@shared/store/configuration'
import { FeatureFlags } from '@shared/plugins/posthog/feature-flags'
import get from 'just-safe-get'
import {
  HouseIcon,
  GraphIcon,
  InventoryIcon,
  AccountingIcon,
  LoyaltyIcon,
  DashboardPlusIcon,
  CustomersIcon,
  StaffIdIcon,
  CalendarCheckIcon,
  DiningTableIcon,
  OfficeBuildingIcon,
  LivehubsIcon,
  DocumentIcon,
  TimerIcon,
  UtilitiesIcon,
  SettingsIcon,
  TagIcon
} from '@shared/assets/icons'

/**
 * Visibility of a nav item is driven by three independent properties:
 * - `enabled`     — account entitlement, from the server driven `featureConfig`.
 * - `meta.scopes` — the user's permissions.
 * - `rolloutFlag` — rollout gating, resolved through Posthog. Declare a key from the `FeatureFlags` registry
 *   and the item disappears until that flag resolves enabled for the merchant. Nothing else
 *   to wire up: the sidebar resolves every flag and filters the tree. Gate the matching route
 *   with the same key via `meta.rolloutFlag` in `th-router.js`, or it stays reachable by URL.
 */
export default function ({ featureConfig, customDashboards } = {}) {
  const authStore = useAuthStore()
  const configStore = useConfigurationStore()
  const config = configStore.tenantConfig
  const isMultitenant = get(config, 'metadata.fiskaly_mode') === 'multitenant'

  return [
    ...handleHook('nav', 'first', customDashboards),
    {
      route: '/home',
      name: 'home',
      icon: HouseIcon,
      enabled: true,
      key: 'nav.main.items.home.title',
      children: []
    },
    {
      route: '/reports',
      name: 'reports',
      icon: GraphIcon,
      enabled: handleAtLeastSome(featureConfig, 'reports'),
      key: 'nav.main.items.reports.title',
      meta: {
        scopes: [
          'reports',
          'reports_financial_accounting',
          'reports_statistics',
          'reports_exports_manager'
        ]
      },
      children: [
        ...handleHook('nav.reports', 'first', customDashboards),
        {
          route: '/financial_accounting',
          name: 'reports_financial_accounting',
          enabled: true,
          key: 'nav.main.items.reports.financial_accounting.title',
          meta: {
            scopes: ['reports_financial_accounting']
          }
        },
        {
          route: '/statistics',
          name: 'reports_statistics',
          enabled: handleAtLeastSome(featureConfig, 'reports.statistics'),
          key: 'nav.main.items.reports.statistics',
          meta: {
            scopes: ['reports_statistics']
          }
        },
        {
          route: '/exports_manager',
          name: 'reports_exports_manager',
          enabled: handleAtLeastSome(featureConfig, 'reports.exports'),
          key: 'nav.main.items.reports.exports',
          meta: {
            scopes: ['reports_exports_manager']
          }
        },
        ...handleHook('nav.reports', 'last', customDashboards)
      ]
    },
    {
      route: '/inventory',
      name: 'inventory',
      icon: InventoryIcon,
      enabled: handleAtLeastSome(featureConfig, 'inventory'),
      key: 'nav.main.items.inventory.title',
      meta: {
        scopes: ['inventory']
      },
      children: [
        {
          path: '',
          redirect: '/stock_manager'
        },
        {
          route: '/stock_manager',
          name: 'ims-stock_manager',
          enabled: true,
          key: 'nav.main.items.inventory.stock_manager',
          meta: {
            scopes: ['inventory']
          }
        },
        {
          route: '/stock',
          name: 'ims-stock',
          enabled: true,
          key: 'nav.main.items.inventory.stock_overview',
          meta: {
            scopes: [
              'inventory:stock_movements:create',
              'inventory:stock_movements:export'
            ]
          }
        },
        {
          route: '/stock_notifications',
          name: 'ims-stock_notifications',
          enabled: true,
          key: 'nav.main.items.inventory.stock_notifications',
          meta: {
            scopes: []
          }
        },
        {
          route: '/reasons',
          name: 'ims-reasons',
          enabled: true,
          key: 'nav.main.items.inventory.reasons',
          meta: {
            scopes: ['inventory:reasons:read']
          }
        },
        {
          route: '/processes',
          name: 'ims-processes',
          enabled: true,
          key: 'nav.main.items.inventory.processes',
          meta: {
            scopes: ['inventory:processes:read']
          }
        },
        {
          route: '/stock_takings',
          name: 'ims-stock-takings',
          enabled: true,
          key: 'nav.main.items.inventory.stock_takings',
          meta: {
            scopes: ['inventory:stock_takings:read']
          }
        }
      ]
    },
    {
      route: '/accounting',
      name: 'accounting',
      icon: AccountingIcon,
      enabled: handleAtLeastSome(featureConfig, 'accounting'),
      key: 'nav.main.items.accounting.title',
      meta: {
        scopes: ['accounting']
      },
      children: []
    },
    {
      route: '/products',
      name: 'products',
      icon: TagIcon,
      enabled: handleAtLeastSome(featureConfig, 'products'),
      key: 'nav.main.items.products.title',
      meta: {
        scopes: ['products']
      },
      children: [
        ...handleHook('nav.products', 'first', customDashboards),
        {
          path: '',
          redirect: '/manager'
        },
        {
          route: '/manager',
          name: 'products-manager',
          key: 'nav.main.items.products.manager',
          enabled: handleAtLeastSome(featureConfig, 'products.manager'),
          meta: {
            scopes: ['products:read']
          }
        },
        {
          route: '/product_groups',
          name: 'products-product-groups',
          key: 'nav.main.items.products.product_groups',
          enabled: handleAtLeastSome(featureConfig, 'products.product_groups'),
          meta: {
            scopes: ['products:product_groups:read']
          }
        },
        {
          route: '/addon-groups',
          name: 'products-addon-groups',
          key: 'nav.main.items.products.addon_groups',
          enabled: authStore.isGastro || authStore.isLitePos,
          meta: {
            scopes: ['products:addon_groups:read']
          }
        },
        {
          route: '/product_templates',
          name: 'products-product-templates',
          key: 'nav.main.items.products.product_templates',
          enabled: handleAtLeastSome(
            featureConfig,
            'products.product_templates'
          ),
          meta: {
            scopes: ['products:product_templates:read']
          }
        },
        {
          route: '/product_service_questions',
          name: 'products-service-questions',
          key: 'nav.main.items.products.services',
          enabled: handleAtLeastSome(featureConfig, 'products.service'),
          meta: {
            scopes: ['products:service_questions:read']
          }
        },
        {
          route: '/discounts',
          name: 'products-discounts-groups',
          key: 'nav.main.items.products.discounts',
          enabled: handleAtLeastSome(featureConfig, 'products.discounts'),
          meta: {
            scopes: ['products:discounts:read']
          }
        },
        {
          route: '/pricebooks',
          name: 'products-pricebooks',
          key: 'nav.main.items.products.pricebooks',
          enabled: handleAtLeastSome(featureConfig, 'products.pricebooks'),
          meta: {
            scopes: ['products:pricebooks:read']
          }
        },
        {
          route: '/promotions',
          name: 'products-promotions',
          key: 'nav.main.items.products.promotions',
          enabled: handleAtLeastSome(featureConfig, 'products.promotions'),
          meta: {
            scopes: ['products:promotions:read']
          }
        },
        {
          route: '/tags',
          name: 'products-tags',
          key: 'nav.main.items.products.tags',
          enabled: handleAtLeastSome(featureConfig, 'products.tags'),
          meta: {
            scopes: ['products:tags:read']
          }
        },
        ...handleHook('nav.products', 'last', customDashboards)
      ]
    },
    {
      route: '/loyalty',
      name: 'loyalty',
      icon: LoyaltyIcon,
      enabled: handleAtLeastSome(featureConfig, 'loyalty'),
      key: 'nav.main.items.loyalty.title',
      meta: {
        scopes: ['loyalty']
      },
      children: [
        {
          route: '/vouchers',
          name: 'loyalty-vouchers',
          enabled:
            handleAtLeastSome(featureConfig, 'loyalty.vouchers') ||
            handleAtLeastSome(featureConfig, 'loyalty.voucher_systems'),
          key: 'nav.main.items.loyalty.all',
          meta: {
            scopes: ['loyalty:vouchers:read', 'loyalty:voucher_systems:read']
          }
        },
        {
          route: '/abocard_systems',
          name: 'loyalty-abocard-systems',
          enabled: handleAtLeastSome(featureConfig, 'loyalty.abocard'),
          key: 'nav.main.items.loyalty.abocard_systems',
          meta: {
            scopes: ['loyalty:abocard_systems:read']
          }
        },
        {
          route: '/logs',
          name: 'loyalty-vouchers-logs',
          enabled: handleAtLeastSome(featureConfig, 'loyalty.vouchers'),
          key: 'nav.main.items.loyalty.logs',
          meta: {
            scopes: ['loyalty:logs']
          }
        }
      ]
    },
    {
      route: '/resources',
      name: 'resources',
      icon: DashboardPlusIcon,
      enabled: handleAtLeastSome(featureConfig, 'resources'),
      key: 'nav.main.items.resources.title',
      meta: {
        scopes: ['resources']
      },
      children: [
        {
          path: '',
          redirect: '/branches'
        },
        {
          route: '/branches',
          name: 'resources-branches',
          enabled: handleAtLeastSome(featureConfig, 'resources.branches'),
          key: 'nav.main.items.resources.branches',
          meta: {
            scopes: ['resources:branches:read']
          }
        },
        {
          route: '/branch_groups',
          name: 'resources-branch-groups',
          enabled: handleAtLeastSome(featureConfig, 'resources.branch_groups'),
          key: 'nav.main.items.resources.branch_groups',
          meta: {
            scopes: ['resources:branch_groups:read']
          }
        },
        {
          route: '/warehouses',
          name: 'resources-warehouses',
          enabled: handleAtLeastSome(featureConfig, 'resources.warehouses'),
          key: 'nav.main.items.resources.warehouses',
          meta: {
            scopes: ['resources:warehouses:read']
          }
        },
        {
          route: '/registers',
          name: 'resources-registers',
          enabled: handleAtLeastSome(featureConfig, 'resources.registers'),
          key: 'nav.main.items.resources.registers',
          meta: {
            scopes: ['resources:registers:read']
          }
        },
        {
          route: '/licenses',
          name: 'resources-licenses',
          deprecated: true,
          enabled: handleAtLeastSome(featureConfig, 'resources.licenses'),
          key: 'nav.main.items.resources.licenses',
          meta: {
            scopes: ['resources:licenses:read']
          }
        },
        {
          route: '/devices',
          name: 'resources-devices',
          deprecated: true,
          enabled: handleAtLeastSome(featureConfig, 'resources.devices'),
          key: 'nav.main.items.resources.devices',
          meta: {
            scopes: ['resources:devices:read']
          }
        },
        {
          route: '/device_groups',
          name: 'resources-device-groups',
          enabled: handleAtLeastSome(featureConfig, 'resources.device_groups'),
          key: 'nav.main.items.resources.device_groups',
          meta: {
            scopes: ['resources:devices:read']
          }
        }
      ]
    },
    {
      route: '/livehubs',
      name: 'livehubs',
      icon: LivehubsIcon,
      enabled:
        handleAtLeastSome(featureConfig, 'resources.devices') ||
        handleAtLeastSome(featureConfig, 'utilities.cms.contents'),
      key: 'nav.main.items.livehubs.title',
      meta: {
        scopes: ['livehubs:livehub_devices:read', 'utilities:cms:read']
      },
      children: [
        {
          route: '/devices',
          name: 'livehubs-devices',
          enabled: handleAtLeastSome(featureConfig, 'resources.devices'),
          key: 'nav.main.items.livehubs.devices',
          meta: {
            scopes: ['livehubs:livehub_devices:read']
          }
        },
        {
          route: '/media',
          name: 'livehubs-media',
          enabled: handleAtLeastSome(featureConfig, 'utilities.cms.contents'),
          key: 'nav.main.items.livehubs.media',
          meta: {
            scopes: ['utilities:cms:read']
          }
        },
        {
          route: '/templates',
          name: 'livehubs-templates',
          enabled: handleAtLeastSome(
            featureConfig,
            'utilities.cms.content_templates'
          ),
          key: 'nav.main.items.livehubs.templates',
          meta: {
            scopes: ['utilities:cms:read']
          }
        }
      ]
    },
    {
      route: '/customers',
      name: 'customers',
      icon: CustomersIcon,
      enabled: handleAtLeastSome(featureConfig, 'customers.crm'),
      key: 'nav.main.items.customers.title',
      meta: {
        scopes: ['customers']
      },
      children: []
    },
    {
      route: '/staff',
      name: 'staff',
      icon: StaffIdIcon,
      enabled: handleAtLeastSome(featureConfig, 'staff'),
      key: 'nav.main.items.staff.title',
      meta: {
        //TODO: find why its shown on scope['staff']
        //Fix for no permission
        scopes: ['staff:read', 'staff:groups:read', 'staff:shift_plans:read']
      },
      children: [
        {
          route: '/manager',
          name: 'staff',
          enabled: true,
          key: 'nav.main.items.staff.all',
          meta: {
            scopes: ['staff:read']
          }
        },
        {
          route: '/staff_groups',
          name: 'staff-groups',
          enabled: true,
          key: 'nav.main.items.staff.staff-groups',
          meta: {
            scopes: ['staff:groups:read']
          }
        },
        {
          route: '/shift_plan',
          name: 'shift-plan',
          enabled: handleAtLeastSome(featureConfig, 'reservations'),
          key: 'nav.main.items.staff.shift_plan',
          meta: {
            scopes: ['staff:shift_plans:read']
          }
        }
      ]
    },
    {
      route: '/floor_layouts',
      name: 'floor_layouts',
      icon: DiningTableIcon,
      enabled: handleAtLeastSome(featureConfig, 'gastro.gastro_basic'),
      key: 'nav.main.items.floor_layouts.title',
      meta: {
        scopes: ['utilities:floor_layouts:read']
      },
      children: []
    },
    {
      route: '/reservations',
      name: 'reservations',
      icon: CalendarCheckIcon,
      enabled:
        handleAtLeastSome(featureConfig, 'reservations') ||
        handleAtLeastSome(featureConfig, 'gastro.table_reservations'),
      key: 'nav.main.items.reservations.title',
      children: [
        {
          route: '/calendar',
          name: 'calendar',
          enabled:
            handleAtLeastSome(featureConfig, 'reservations') ||
            handleAtLeastSome(featureConfig, 'gastro.table_reservations'),
          key: 'nav.main.items.reservations.calendar'
        },
        {
          route: '/service_category',
          name: 'service-category',
          enabled: handleAtLeastSome(featureConfig, 'reservations'),
          key: 'nav.main.items.reservations.service_category',
          meta: {
            scopes: ['reservations:service_categories:read']
          }
        },
        {
          route: '/services',
          name: 'services',
          enabled: handleAtLeastSome(featureConfig, 'reservations'),
          key: 'nav.main.items.reservations.services',
          meta: {
            scopes: ['reservations:services:read']
          }
        },
        {
          route: '/service_steps',
          name: 'service-steps',
          enabled: handleAtLeastSome(featureConfig, 'reservations'),
          rolloutFlag: FeatureFlags.SERVICE_STEPS,
          key: 'nav.main.items.reservations.service_steps',
          meta: {
            scopes: ['reservations:service_steps:read']
          }
        }
      ]
    },
    {
      route: '/supplier-management',
      name: 'supplier-management',
      icon: OfficeBuildingIcon,
      enabled: true,
      key: 'nav.main.items.suppliers.title',
      meta: {
        scopes: ['supplier_management', 'supplier_management:suppliers']
      },
      children: [
        {
          route: '/suppliers',
          name: 'supplier-manager',
          enabled: true,
          key: 'nav.main.items.supplier.manager',
          meta: {
            scopes: [
              'supplier_management:suppliers',
              'supplier_management:suppliers:read'
            ]
          }
        },
        {
          route: '/purchase-orders',
          name: 'purchase-orders',
          enabled: true,
          key: 'nav.main.items.supplier.purchase_orders',
          meta: {
            scopes: [
              'supplier_management:purchase_orders',
              'supplier_management:purchase_orders:read'
            ]
          }
        }
      ]
    },
    {
      route: '/documents',
      name: 'documents',
      icon: DocumentIcon,
      enabled: handleAtLeastSome(featureConfig, 'documents'),
      key: 'nav.main.items.documents.title',
      meta: {
        scopes: ['documents', 'documents:read']
      },
      children: [
        {
          route: '/overview',
          name: 'documents-overview',
          enabled: true,
          key: 'nav.main.items.documents.overview',
          meta: {
            scopes: ['documents:read']
          }
        }
      ]
    },
    {
      route: '/timetracking',
      name: 'timetracking',
      icon: TimerIcon,
      enabled: handleAtLeastSome(featureConfig, 'timetracking'),
      key: 'nav.main.items.timetracking.title',
      meta: {
        scopes: ['timetracking']
      },
      children: []
    },
    {
      route: '/utilities',
      name: 'utilities',
      icon: UtilitiesIcon,
      enabled: handleAtLeastSome(featureConfig, 'utilities'),
      key: 'nav.main.items.utilities.title',
      meta: {
        scopes: ['utilities']
      },
      children: [
        {
          route: '/audits',
          name: 'utilities-audits',
          enabled: handleAtLeastSome(featureConfig, 'utilities.audits'),
          key: 'nav.main.items.utilities.audits',
          meta: {
            scopes: ['utilities:audits']
          }
        },
        {
          route: '/tse',
          name: 'tse',
          enabled:
            // This feature is not compatible at the moment with multitenant accounts, so the view is disabled.
            isMultitenant === false &&
            handleAtLeastSome(
              featureConfig,
              'utilities.kassensichv_submissions'
            ),
          key: 'nav.main.items.utilities.tse'
        },
        {
          route: '/document_designer',
          name: 'document-designer',
          enabled: handleAtLeastSome(
            featureConfig,
            'utilities.document_designer'
          ),
          key: 'nav.main.items.utilities.document_designer',
          meta: {
            scopes: ['utilities:document_designer:read']
          }
        },
        {
          route: '/custom_dashboards',
          name: 'utilities-custom-dashboard',
          enabled: handleAtLeastSome(
            featureConfig,
            'utilities.custom_dashboards'
          ),
          key: 'nav.main.items.utilities.custom_dashboards',
          meta: {
            scopes: ['utilities:custom_dashboard:read']
          }
        },
        {
          route: '/pos_functions',
          name: 'utilities-pos-functions',
          enabled: handleAtLeastSome(featureConfig, 'utilities.pos_functions'),
          key: 'nav.main.items.utilities.pos_functions',
          meta: {
            scopes: ['utilities:pos_functions:read']
          }
        },
        {
          route: '/notifications',
          name: 'notifications',
          enabled: handleAtLeastSome(featureConfig, 'utilities.notifications'),
          key: 'nav.main.items.utilities.notifications',
          meta: {
            scopes: ['utilities:notifications:create']
          }
        },
        {
          route: '/favourites',
          name: 'favourites',
          enabled: handleAtLeastSome(featureConfig, 'utilities.favourites'),
          key: 'nav.main.items.utilities.favourites',
          meta: {
            scopes: ['utilities:favourites:read']
          }
        },
        {
          route: '/export_history',
          name: 'export-history',
          enabled: true,
          key: 'nav.main.items.utilities.export_history',
          meta: {
            scopes: ['utilities:export_history:read']
          }
        }
      ]
    },
    {
      route: '/settings',
      name: 'settings',
      icon: SettingsIcon,
      enabled: handleAtLeastSome(featureConfig, 'settings'),
      key: 'nav.main.items.settings.title',
      meta: {
        scopes: ['settings']
      },
      children: [
        {
          route: '/dashboard',
          name: 'settings-dashboard',
          enabled: true,
          key: 'nav.main.items.settings.general',
          meta: {
            scopes: ['settings:dashboard:read']
          }
        },
        {
          route: '/reservations',
          name: 'settings-reservations',
          enabled: handleAtLeastSome(featureConfig, 'reservations'),
          key: 'nav.main.items.settings.reservations',
          meta: {
            scopes: ['settings:reservations:read']
          }
        },
        {
          route: '/gastro-reservations',
          name: 'settings-gastro-reservations',
          enabled: handleAtLeastSome(
            featureConfig,
            'gastro.table_reservations'
          ),
          key: 'nav.main.items.settings.gastro_reservations',
          meta: {
            scopes: ['settings:gastro_reservations:read']
          }
        },
        {
          route: '/account',
          name: 'settings-dashboard',
          enabled: true,
          key: 'nav.main.items.settings.account',
          meta: {
            scopes: ['settings:account:read']
          }
        },
        {
          route: '/timetracking',
          name: 'settings-timetracking',
          enabled: handleAtLeastSome(featureConfig, 'settings.timetracking'),
          key: 'nav.main.items.settings.timetracking',
          meta: {
            scopes: ['settings:timetracking:read']
          }
        },
        {
          route: '/pos',
          name: 'settings-pos',
          enabled: true,
          key: 'nav.main.items.settings.pos',
          meta: {
            scopes: ['settings:pos:read']
          }
        },
        {
          route: '/users',
          name: 'settings-users',
          enabled: true,
          key: 'nav.main.items.settings.users',
          meta: {
            scopes: ['settings:users:read']
          }
        },
        {
          route: '/webhooks',
          name: 'webhooks',
          enabled: handleAtLeastSome(featureConfig, 'webhooks'),
          key: 'nav.main.items.webhooks.title',
          meta: {
            scopes: ['webhooks']
          },
          children: []
        },
        {
          route: '/dashboard/remote-ordering-api',
          name: 'settings-remote-ordering-api-nav',
          enabled: handleAtLeastSome(featureConfig, 'settings.remote_ordering'),
          key: 'nav.main.items.settings.remote_ordering',
          activeRoutes: ['/settings/remote-ordering-api'],
          meta: {
            scopes: ['settings:dashboard:read']
          }
        },
        {
          route: '/integrations',
          name: 'integrations',
          enabled: true,
          key: 'nav.main.items.integrations.title',
          meta: {
            scopes: ['integrations']
          },
          children: []
        }
      ]
    },
    ...handleHook('nav', 'last', customDashboards)
  ]
}
